import { Node, Edge, useViewport, useStore, useReactFlow } from "@xyflow/react";
import styles from "./DevTools.module.css";
import {
  Alert,
  Button,
  Grid,
  Icon,
  Spacer,
  Spinner,
  Surface,
  Text,
  TextInput,
  ToggleSwitch,
} from "@zapier/design-system";
import { Button as BetaButton } from "@zapier/design-system-beta";
import { useCallback, useState, useEffect, Suspense } from "react";
import {
  nodesAtom,
  edgesAtom,
  canvasAtom,
  Canvas,
  ZapsMap,
  shouldShowStepIdsAtom,
  isViewOnlyModeAtom,
  togglesAtomResettable,
  zapsAtom_SUSPENDS,
  generateZslSetter,
  generateZslErrorAtom,
  canvasNameAtom,
  zslAtom,
  makeImagesPublicSetter,
  ZSL,
  ZapMetadata,
  zslCreatorBaseUrlAtom,
  saveCanvasSetter,
  showJotaiDevtoolsAtom,
  isZapNode,
  isDefaultNode,
  isTextNode,
} from "@zapier/canvas-state";
import { zslToCanvasErrorAtom, zslToCanvasPostSetter } from "@zapier/streaming";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useResetAtom } from "jotai/utils";
import { useComposer } from "@zapier/agent-system";

import { CopyToClipboardTextarea } from "../../components/CopyToClipboardTextarea";
import { ClearCanvasButton } from "../../components/ClearCanvasButton";
import { NewCanvasButton } from "../../components/NewCanvasButton";
import { composeCanvasForTemplate } from "../../utils/composeCanvasForTemplate";
import { toKebabCase } from "../../../../service/src/app/utils/stringUtils";
import { SELECTED_CHAT_ID_QUERY_PARAM } from "@zapier/canvas-constants";

// Local storage key for saved chat states
const SAVED_CHAT_STATES_KEY = "zapier-saved-chat-states";

// Type for saved chat state
type SavedChatState = {
  id: string;
  title: string;
  timestamp: number;
  messages: any[];
  additionalData: any;
  canvas: Partial<Canvas>;
  nodes: Node[];
  edges: Edge[];
  zsl?: ZSL;
};

type Data = Node[] | Edge[] | Partial<Canvas> | ZapsMap | ZSL;
const serialize = (data: Data) => JSON.stringify(data, null, 2);

const isNotEmpty = (obj: object) => Object.keys(obj).length > 0;

const selector = ({ userSelectionActive, userSelectionRect }: any) => ({
  userSelectionActive,
  userSelectionRect,
});

function getZapCount(zaps: Record<string, ZapMetadata>): number {
  return Object.keys(zaps).length;
}

const SuspendedZapsSection = () => {
  const zaps = useAtomValue(zapsAtom_SUSPENDS);
  const rawZapsData = serialize(zaps);
  const hasZaps = isNotEmpty(zaps);

  if (!hasZaps) {
    return null;
  }

  const zapCount = getZapCount(zaps);

  return (
    <Surface backgroundColor="PrimeWhite" borderColor="GrayWarm5">
      <div className={styles.devtoolsInner}>
        <Text type="Body3">Zaps ({zapCount})</Text>
        <CopyToClipboardTextarea value={rawZapsData} />
      </div>
    </Surface>
  );
};

const ZapsTextArea = () => {
  return (
    <Suspense fallback={null}>
      <SuspendedZapsSection />
    </Suspense>
  );
};

const ON_VALUES = [true, "default"];

const DevTools = () => {
  const [zslCreatorBaseUrl, setZslCreatorBaseUrl] = useAtom(
    zslCreatorBaseUrlAtom,
  );
  const [showJotaiDevtools, setShowJotaiDevtools] = useAtom(
    showJotaiDevtoolsAtom,
  );

  const {
    edges: canvasEdges,
    nodes: canvasNodes,
    ...rest
  } = useAtomValue(canvasAtom);
  const zsl = useAtomValue(zslAtom);
  const store = useStore(selector);

  const { fitView } = useReactFlow();
  const saveToBackend = useSetAtom(saveCanvasSetter);
  const nodes = useAtomValue(nodesAtom);
  const edges = useAtomValue(edgesAtom);
  const [shouldShowStepIds, setShouldShowStepIds] = useAtom(
    shouldShowStepIdsAtom,
  );
  const setNodes = useSetAtom(nodesAtom);
  const setEdges = useSetAtom(edgesAtom);
  const setZsl = useSetAtom(zslAtom);

  const [toggles, setToggles] = useAtom(togglesAtomResettable);
  const resetAllFeatureFlags = useResetAtom(togglesAtomResettable);

  const togglesEntries = Object.entries(toggles);
  const [isViewOnlyMode, setIsViewOnlyMode] = useAtom(isViewOnlyModeAtom);
  const canvasName = useAtomValue(canvasNameAtom);

  const handleResetAllFeatureFlags = useCallback(() => {
    resetAllFeatureFlags();
  }, [resetAllFeatureFlags]);

  const createFeatureFlagRows = () => {
    return togglesEntries.map(([key, value]) => {
      const isSimpleFlag =
        typeof value === "string" || typeof value === "boolean";
      const isFlagWithConfig =
        typeof value === "object" && value !== null && "isOn" in value;

      return (
        <li key={key} className={styles.featureFlagRowWrapper}>
          <div className={styles.featureFlagRow}>
            <Text type="SmallPrint3">{key}</Text>
            <div className={styles.toggleWrapper}>
              <ToggleSwitch
                checked={Boolean(
                  isFlagWithConfig
                    ? (value as any).isOn
                    : isSimpleFlag
                      ? ON_VALUES.includes(value)
                      : false,
                )}
                onChange={() => {
                  if (isFlagWithConfig) {
                    setToggles((prevToggles) => ({
                      ...prevToggles,
                      [key]: {
                        ...(value as object),
                        isOn: !(value as any).isOn,
                      },
                    }));
                  } else if (isSimpleFlag) {
                    let oppositeValue;
                    if (typeof value === "string") {
                      oppositeValue = value === "default" ? "cool" : "default"; // Toggle between 'default' and another non-default string
                    } else {
                      oppositeValue = !value; // Toggle between true and false
                    }
                    setToggles((prevToggles) => ({
                      ...prevToggles,
                      [key]: oppositeValue,
                    }));
                  } else {
                    setToggles((prevToggles) => ({
                      ...prevToggles,
                      [key]: true,
                    }));
                  }
                }}
              />
            </div>
          </div>
        </li>
      );
    });
  };

  const viewport = useViewport();

  const rawCanvasData = serialize(rest);
  const rawNodesData = serialize(nodes);
  const rawEdgesData = serialize(edges);
  const rawZslData = zsl ? serialize(zsl) : undefined;
  const rawReactFlowStore = serialize(store);

  const hasNodes = nodes.length > 0;
  const hasEdges = edges.length > 0;

  const convertZslToCanvas = useSetAtom(zslToCanvasPostSetter);
  const [localZsl, setLocalZsl] = useState("");
  const [zslToCanvasError, setZslToCanvasError] = useAtom(zslToCanvasErrorAtom);

  const generateZsl = useSetAtom(generateZslSetter);
  const [isGeneratingTemplate, setIsGeneratingTemplate] = useState(false);
  const [generateZslError, setGenerateZslError] = useAtom(generateZslErrorAtom);
  const [template, setTemplate] = useState(null);

  const [isAtBottom, setIsAtBottom] = useState(false);

  const handleChangeZslToCanvas = (event: any) => {
    setLocalZsl(event.target.value);
  };

  const handleSubmitZslToCanvas = async () => {
    try {
      //TODO: Ideally, we would do the saving inside of the Atom, which is not possible currently
      await convertZslToCanvas(localZsl);
    } catch (error) {
      console.error("Error converting ZSL to Canvas:", error);
    }
  };

  const makeImagesPublic = useSetAtom(makeImagesPublicSetter);

  const handleSubmitExportCanvas = async () => {
    const assetIds = {
      zapIds: [] as string[],
      tableIds: [] as string[],
      chatbotIds: [] as string[],
      interfaceIds: [] as string[],
    };

    nodes.forEach((node) => {
      if (
        isZapNode(node) &&
        node.data.zapId &&
        !assetIds.zapIds.includes(node.data.zapId)
      ) {
        assetIds.zapIds.push(node.data.zapId);
      } else if (
        isDefaultNode(node) &&
        node.data.tableId &&
        !assetIds.tableIds.includes(node.data.tableId)
      ) {
        assetIds.tableIds.push(node.data.tableId);
      } else if (
        isDefaultNode(node) &&
        node.data.chatbotId &&
        !assetIds.chatbotIds.includes(node.data.chatbotId)
      ) {
        assetIds.chatbotIds.push(node.data.chatbotId);
      } else if (
        isDefaultNode(node) &&
        node.data.projectId &&
        !assetIds.interfaceIds.includes(node.data.projectId)
      ) {
        assetIds.interfaceIds.push(node.data.projectId);
      }
    });

    // Collect all image IDs from nodes that aren't already public
    const imageIds: string[] = [];
    nodes.forEach((node) => {
      const extractImageIds = (content: any) => {
        if (
          content?.attrs?.src?.includes("/api/canvas/v1/canvases") &&
          !content.attrs.src.includes("/api/canvas/v1/canvases/public") // Skip if already public
        ) {
          const imageId = content.attrs.src.split("/").pop();
          if (imageId) imageIds.push(imageId);
        }
        if (content.content) {
          content.content.forEach((item: any) => extractImageIds(item));
        }
      };

      if (isDefaultNode(node) && node.data.notesJson?.content) {
        node.data.notesJson.content.forEach(extractImageIds);
      }
      if (isTextNode(node) && node.data.label?.content) {
        node.data.label.content.forEach(extractImageIds);
      }
    });

    try {
      setIsGeneratingTemplate(true);

      // Only make images public if there are images to process
      let imageIdMap: Record<string, string> = {};
      if (imageIds.length > 0) {
        imageIdMap = await makeImagesPublic(imageIds);
      }

      // Create new nodes with public image URLs
      const nodesWithPublicImages = nodes.map((node) => {
        const newNode = JSON.parse(JSON.stringify(node)); // Deep clone the node to avoid mutations
        const updateContent = (content: any) => {
          if (content?.attrs?.src?.includes("/api/canvas/v1/canvases")) {
            const imageId = content.attrs.src.split("/").pop();
            if (imageId && imageIdMap[imageId]) {
              content.attrs.src = `/api/canvas/v1/canvases/public/images/${imageIdMap[imageId]}`;
            }
          }
          if (content.content) {
            content.content.forEach(updateContent);
          }
        };

        if (newNode.data.notesJson?.content) {
          newNode.data.notesJson.content.forEach(updateContent);
        }
        if (newNode.data.label?.content) {
          newNode.data.label.content.forEach(updateContent);
        }
        return newNode;
      });

      const data = await generateZsl(assetIds);
      const template = data.template;
      template.redirectTo = "_CANVAS_1";
      template.name = canvasName || "New Template";
      template.canvases = [
        composeCanvasForTemplate(
          canvasName,
          nodesWithPublicImages,
          edges,
          data.resourceMap,
        ),
      ];
      setTemplate(template);
      setIsGeneratingTemplate(false);
    } catch (error) {
      console.error("Error exporting Canvas:", error);
      setIsGeneratingTemplate(false);
    }
  };

  const handleSubmitDownloadTemplate = () => {
    if (!template) return;

    const jsonContent = JSON.stringify(template, null, 2);
    const blob = new Blob([jsonContent], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.download = `${toKebabCase(canvasName) || "untitled"}-template.json`;
    a.href = url;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    setTemplate(null);
  };

  useEffect(() => {
    const contentDiv = document.querySelector(
      'section[class*="_drawer_"] *[class*="_content_"]',
    );

    const checkIfAtBottom = () => {
      if (!contentDiv) return;

      const bottomBuffer = 200; // Pixels from the bottom considered "at the bottom"

      const isBottom =
        contentDiv.scrollHeight - contentDiv.clientHeight <=
        contentDiv.scrollTop + bottomBuffer;

      setIsAtBottom(isBottom);
    };

    contentDiv?.addEventListener("scroll", checkIfAtBottom);

    // Clean up the event listener when the component unmounts
    return () => contentDiv?.removeEventListener("scroll", checkIfAtBottom);
  }, [setIsAtBottom]);

  const handleScrollToBottom = useCallback(() => {
    const contentDiv = document.querySelector(
      'section[class*="_drawer_"] *[class*="_content_"]',
    ) as HTMLElement;

    if (contentDiv) {
      contentDiv.style.scrollBehavior = "smooth";
      contentDiv.scrollTop = contentDiv.scrollHeight;
    }
  }, []);

  const { messages, additionalData, setMessages, setAdditionalData } =
    useComposer();

  const handleSaveMessageHistory = useCallback(() => {
    // Empty callback that doesn't use dependencies
  }, []);

  const [messagesInput, setMessagesInput] = useState("");
  const [additionalDataInput, setAdditionalDataInput] = useState("");
  const [parseError, setParseError] = useState<string | null>(null);
  const [savedTitle, setSavedTitle] = useState("");
  const [savedStates, setSavedStates] = useState<SavedChatState[]>([]);

  // Load saved states from localStorage
  useEffect(() => {
    try {
      const savedData = localStorage.getItem(SAVED_CHAT_STATES_KEY);
      if (savedData) {
        const parsed = JSON.parse(savedData);
        if (Array.isArray(parsed)) {
          setSavedStates(parsed);
        } else {
          console.error(
            "[DevTools] Saved data is not an array:",
            typeof parsed,
          );
        }
      } else {
        console.error("[DevTools] No saved chat states found in localStorage");
      }
    } catch (error) {
      console.error("[DevTools] Failed to load saved chat states:", error);
    }
  }, []);

  // Load a saved chat state
  const handleLoadChatState = useCallback(
    (state: SavedChatState) => {
      try {
        // Deep clone the data to ensure we break any reference issues
        const clonedMessages = JSON.parse(JSON.stringify(state.messages));
        const clonedData = JSON.parse(JSON.stringify(state.additionalData));
        const clonedNodes = JSON.parse(JSON.stringify(state.nodes));
        const clonedEdges = JSON.parse(JSON.stringify(state.edges));
        const clonedCanvas = JSON.parse(JSON.stringify(state.canvas));
        const clonedZsl = state.zsl
          ? JSON.parse(JSON.stringify(state.zsl))
          : undefined;

        // Update chat state
        setMessages(clonedMessages);
        setAdditionalData(clonedData);

        // Update canvas state
        const updatedCanvas = {
          ...clonedCanvas,
          nodes: clonedNodes,
          edges: clonedEdges,
        };
        saveToBackend(updatedCanvas);

        // Update individual atoms
        setNodes(clonedNodes);
        setEdges(clonedEdges);
        if (clonedZsl) {
          setZsl(clonedZsl);
        }

        // Show feedback
        setParseError(`Successfully loaded "${state.title}"`);
        setTimeout(() => setParseError(null), 2000);

        // Fit the view to show the loaded canvas
        setTimeout(() => fitView(), 100);
      } catch (error) {
        console.error("[DevTools] Failed to load chat state:", error);
        setParseError(
          `Failed to load chat state: ${error instanceof Error ? error.message : String(error)}`,
        );
      }
    },
    [
      setMessages,
      setAdditionalData,
      setNodes,
      setEdges,
      setZsl,
      saveToBackend,
      fitView,
    ],
  );

  // Save current chat state to localStorage
  const handleSaveChatState = useCallback(() => {
    try {
      if (!savedTitle.trim()) {
        setParseError("Please enter a title for the saved state");
        return;
      }

      // Create a unique ID with a random component
      const uniqueId = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

      // Deep clone the data to ensure we don't save references
      const newState: SavedChatState = {
        id: uniqueId,
        title: savedTitle,
        timestamp: Date.now(),
        messages: JSON.parse(JSON.stringify(messages)),
        additionalData: JSON.parse(JSON.stringify(additionalData || {})),
        canvas: JSON.parse(JSON.stringify(rest)),
        nodes: JSON.parse(JSON.stringify(nodes)),
        edges: JSON.parse(JSON.stringify(edges)),
        zsl: zsl ? JSON.parse(JSON.stringify(zsl)) : undefined,
      };

      const updatedStates = [...savedStates, newState];
      const jsonString = JSON.stringify(updatedStates);
      localStorage.setItem(SAVED_CHAT_STATES_KEY, jsonString);
      setSavedStates(updatedStates);
      setSavedTitle("");

      // Show feedback
      setParseError(`Successfully saved "${savedTitle}"`);
      setTimeout(() => setParseError(null), 2000);
    } catch (error) {
      console.error("[DevTools] Failed to save chat state:", error);
      setParseError(
        `Failed to save chat state: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  }, [
    savedTitle,
    messages,
    additionalData,
    savedStates,
    rest,
    nodes,
    edges,
    zsl,
  ]);

  // Delete a saved chat state
  const handleDeleteChatState = useCallback(
    (id: string, event: React.MouseEvent) => {
      event.stopPropagation();
      try {
        const updatedStates = savedStates.filter((state) => state.id !== id);
        localStorage.setItem(
          SAVED_CHAT_STATES_KEY,
          JSON.stringify(updatedStates),
        );
        setSavedStates(updatedStates);
      } catch (error) {
        setParseError(
          `Failed to delete chat state: ${error instanceof Error ? error.message : String(error)}`,
        );
      }
    },
    [savedStates],
  );

  // Generate shareable URL for a chat state
  const generateShareableUrl = useCallback((id: string) => {
    const url = new URL(window.location.href);
    url.searchParams.set(SELECTED_CHAT_ID_QUERY_PARAM, id);
    const shareUrl = url.toString();
    return shareUrl;
  }, []);

  // Copy shareable URL to clipboard
  const copyShareableUrl = useCallback(
    (id: string, event: React.MouseEvent) => {
      event.stopPropagation();
      const url = generateShareableUrl(id);
      navigator.clipboard
        .writeText(url)
        .then(() => {
          setParseError("URL copied to clipboard!");
          setTimeout(() => setParseError(null), 2000);
        })
        .catch((error) => {
          console.error("[DevTools] Failed to copy URL:", error);
          setParseError(
            `Failed to copy URL: ${error instanceof Error ? error.message : String(error)}`,
          );
        });
    },
    [generateShareableUrl],
  );

  const handleUpdateChat = useCallback(() => {
    try {
      // Reset any previous errors
      setParseError(null);

      // Parse messages if provided
      if (messagesInput.trim()) {
        const parsedMessages = JSON.parse(messagesInput);
        if (!Array.isArray(parsedMessages)) {
          throw new Error("Messages must be an array");
        }
        setMessages(parsedMessages);
      }

      // Parse additional data if provided
      if (additionalDataInput.trim()) {
        const parsedData = JSON.parse(additionalDataInput);
        if (typeof parsedData !== "object" || parsedData === null) {
          throw new Error("Additional data must be an object");
        }
        setAdditionalData(parsedData);
      }

      // Clear inputs after successful update
      setMessagesInput("");
      setAdditionalDataInput("");
    } catch (error) {
      setParseError(
        `Failed to parse input: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  }, [messagesInput, additionalDataInput, setMessages, setAdditionalData]);

  return (
    <div className={styles.devtoolsSectionWrapper}>
      {!isAtBottom && (
        <div className={styles.scrollDownButtonWrapper}>
          <BetaButton size="medium" onPress={handleScrollToBottom}>
            <div className={styles.scrollDownButtonTextWrapper}>
              <Icon name="arrowBigDown" size={20} />
              <span>Scroll to bottom</span>
            </div>
          </BetaButton>
        </div>
      )}
      <Surface backgroundColor="PrimeWhite" borderColor="GrayWarm5">
        <div className={styles.devtoolsInner}>
          <div className={styles.flexRow}>
            <Text type="Body3">Show step IDs</Text>
            <div className={styles.toggleWrapper}>
              <ToggleSwitch
                checked={shouldShowStepIds}
                onChange={() => setShouldShowStepIds((prev) => !prev)}
              />
            </div>
          </div>
        </div>
      </Surface>
      {process.env.NODE_ENV !== "production" && (
        <Surface backgroundColor="PrimeWhite" borderColor="GrayWarm5">
          <div className={styles.devtoolsInner}>
            <div className={styles.flexRow}>
              <Text type="Body3">Show Jotai Devtools</Text>
              <div className={styles.toggleWrapper}>
                <ToggleSwitch
                  checked={showJotaiDevtools}
                  onChange={() => setShowJotaiDevtools((prev) => !prev)}
                />
              </div>
            </div>
          </div>
        </Surface>
      )}
      <Surface backgroundColor="PrimeWhite" borderColor="GrayWarm5">
        <div className={styles.devtoolsInner}>
          <div className={styles.flexRow}>
            <Text type="Body3">View-Only Mode</Text>
            <div className={styles.toggleWrapper}>
              <ToggleSwitch
                checked={isViewOnlyMode}
                onChange={() => {
                  setIsViewOnlyMode((prev) => !prev);
                }}
              />
            </div>
          </div>
        </div>
      </Surface>
      {hasNodes && (
        <Surface backgroundColor="PrimeWhite" borderColor="GrayWarm5">
          <div className={styles.devtoolsInner}>
            <Text type="Body3">Nodes ({nodes.length})</Text>
            <CopyToClipboardTextarea value={rawNodesData} />
          </div>
        </Surface>
      )}
      {hasEdges && (
        <Surface backgroundColor="PrimeWhite" borderColor="GrayWarm5">
          <div className={styles.devtoolsInner}>
            <Text type="Body3">Edges ({edges.length})</Text>
            <CopyToClipboardTextarea value={rawEdgesData} />
          </div>
        </Surface>
      )}
      <Surface backgroundColor="PrimeWhite" borderColor="GrayWarm5">
        <div className={styles.devtoolsInner}>
          <Text type="Body3">Canvas</Text>
          <CopyToClipboardTextarea value={rawCanvasData} />
        </div>
      </Surface>
      <ZapsTextArea />
      {rawZslData && (
        <Surface backgroundColor="PrimeWhite" borderColor="GrayWarm5">
          <div className={styles.devtoolsInner}>
            <Text type="Body3">ZSL</Text>
            <CopyToClipboardTextarea value={rawZslData} />
          </div>
        </Surface>
      )}
      <Surface backgroundColor="PrimeWhite" borderColor="GrayWarm5">
        <div className={styles.devtoolsInner}>
          <div className={styles.featureFlagsHeader}>
            <Text type="Body3">Feature Flags</Text>
            <Icon name="actionFlag" size={24} />
          </div>
          <div className={styles.devToolsContentWrapper}>
            <ul className={styles.featureFlagsWrapper}>
              {createFeatureFlagRows()}
            </ul>
            <div className={styles.resetFeatureFlagsButton}>
              <Button size="compact" onClick={handleResetAllFeatureFlags}>
                Reset all
              </Button>
            </div>
          </div>
        </div>
      </Surface>
      <Surface backgroundColor="PrimeWhite" borderColor="GrayWarm5">
        <div className={styles.devtoolsInner}>
          <Text type="Body3">React Flow Store</Text>
          <CopyToClipboardTextarea value={rawReactFlowStore} rows={4} />
        </div>
      </Surface>
      <Surface backgroundColor="PrimeWhite" borderColor="GrayWarm5">
        <div className={styles.devtoolsInner}>
          <Text type="Body3">Viewport</Text>
          <div className={styles.viewportWrapper}>
            <Grid columns={3}>
              <div className={styles.viewportCell}>
                <strong>x:</strong> {viewport.x.toFixed(1)}
              </div>
              <div className={styles.viewportCell}>
                <strong>y:</strong> {viewport.y.toFixed(1)}
              </div>
              <div className={styles.viewportCell}>
                <strong>zoom:</strong> {viewport.zoom.toFixed(1)}
              </div>
            </Grid>
          </div>
        </div>
      </Surface>
      <Surface backgroundColor="PrimeWhite" borderColor="GrayWarm5">
        <div className={styles.devtoolsInner}>
          <Text type="Body3">Actions</Text>
          <div className={styles.devToolsContentWrapperFlex}>
            <ClearCanvasButton />
            <NewCanvasButton />
          </div>
        </div>
      </Surface>
      <Surface backgroundColor="PrimeWhite" borderColor="GrayWarm5">
        <div className={styles.devtoolsInner}>
          <Text type="Body3">Simulate Error</Text>
          <Text type="SmallPrint1" tag="div">
            Manually throw an error, so you can trace it in Datadog.
          </Text>
          <div className={styles.devToolsContentWrapperFlex}>
            <Button
              color="danger"
              size="small"
              onClick={() => {
                throw new Error("Simulated error");
              }}
            >
              Throw Error
            </Button>
          </div>
        </div>
      </Surface>
      <Surface backgroundColor="PrimeWhite" borderColor="GrayWarm5">
        <div className={styles.devtoolsInner}>
          <Text type="Body3">Modify Chat State</Text>
          {parseError && (
            <>
              <Alert
                title={parseError}
                onDismiss={() => setParseError(null)}
                status="error"
              />
              <Spacer height={15}></Spacer>
            </>
          )}
          <div className={styles.devToolsContentWrapperFlex}>
            <Button size="compact" onClick={handleSaveMessageHistory}>
              Save Chat State
            </Button>
          </div>
          <Spacer height={15}></Spacer>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <TextInput
              value={savedTitle}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSavedTitle(e.target.value)
              }
              placeholder="Enter a title for this state"
            />
            <Button size="compact" onClick={handleSaveChatState}>
              Save to Local Storage
            </Button>
          </div>

          {savedStates.length > 0 && (
            <>
              <Spacer height={15}></Spacer>
              <Text type="SmallPrint1" tag="div">
                Saved Chat States:
              </Text>
              <div
                style={{
                  maxHeight: "200px",
                  overflowY: "auto",
                  border: "1px solid #eaeaea",
                  borderRadius: "4px",
                  marginTop: "8px",
                }}
              >
                {savedStates.map((state) => (
                  <button
                    key={state.id}
                    onClick={() => handleLoadChatState(state)}
                    style={{
                      width: "100%",
                      padding: "8px 12px",
                      cursor: "pointer",
                      borderBottom: "1px solid #eaeaea",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      backgroundColor: "white",
                      transition: "background-color 0.2s",
                      border: "none",
                      textAlign: "left",
                    }}
                    // eslint-disable-next-line jsx-a11y/mouse-events-have-key-events
                    onMouseOver={(e) =>
                      (e.currentTarget.style.backgroundColor = "#f5f5f5")
                    }
                    // eslint-disable-next-line jsx-a11y/mouse-events-have-key-events
                    onMouseOut={(e) =>
                      (e.currentTarget.style.backgroundColor = "white")
                    }
                  >
                    <div>
                      <div style={{ fontWeight: "bold" }}>{state.title}</div>
                      <div style={{ fontSize: "12px", color: "#666" }}>
                        {new Date(state.timestamp).toLocaleString()}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <Button
                        size="compact"
                        onClick={(e) => copyShareableUrl(state.id, e)}
                      >
                        <Icon name="actionShare" size={16} />
                      </Button>
                      <Button
                        size="compact"
                        color="danger"
                        onClick={(e) => handleDeleteChatState(state.id, e)}
                      >
                        <Icon name="formX" size={16} />
                      </Button>
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}

          <Spacer height={15}></Spacer>
          <Text type="SmallPrint1" tag="div">
            Messages (JSON array):
          </Text>
          <TextInput
            isMultiline={true}
            rows={8}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              setMessagesInput(e.target.value)
            }
            value={messagesInput}
            placeholder="Paste messages JSON array here"
          />
          <Spacer height={15}></Spacer>
          <Text type="SmallPrint1" tag="div">
            Additional Data (JSON object):
          </Text>
          <TextInput
            isMultiline={true}
            rows={8}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              setAdditionalDataInput(e.target.value)
            }
            value={additionalDataInput}
            placeholder="Paste additional data JSON object here"
          />
          <div className={styles.devToolsContentWrapper}>
            <div className={styles.resetFeatureFlagsButton}>
              <Button size="compact" onClick={handleUpdateChat}>
                Update Chat State
              </Button>
            </div>
          </div>
        </div>
      </Surface>
      <Surface backgroundColor="PrimeWhite" borderColor="GrayWarm5">
        <div className={styles.devtoolsInner}>
          <Text type="Body3">Convert ZSL to Canvas</Text>
          {zslToCanvasError && (
            <>
              <Alert
                title={zslToCanvasError}
                onDismiss={() => setZslToCanvasError(null)}
                status="error"
              />
              <Spacer height={15}></Spacer>
            </>
          )}
          <TextInput
            isMultiline={true}
            rows={20}
            onChange={handleChangeZslToCanvas}
            value={localZsl}
            isErrored={!!zslToCanvasError}
          />
          <div className={styles.devToolsContentWrapper}>
            <div className={styles.resetFeatureFlagsButton}>
              <Button size="compact" onClick={handleSubmitZslToCanvas}>
                Render Canvas
              </Button>
            </div>
          </div>
        </div>
      </Surface>
      <Surface backgroundColor="PrimeWhite" borderColor="GrayWarm5">
        <div className={styles.devtoolsInner}>
          <Text type="Body3">Export Canvas to Template</Text>
          {generateZslError && (
            <>
              <Alert
                title={generateZslError}
                onDismiss={() => setGenerateZslError(null)}
                status="error"
              />
              <Spacer height={15}></Spacer>
            </>
          )}
          <div className={styles.devToolsContentWrapperFlex}>
            {isGeneratingTemplate ? (
              <Button size="small" color="primary" disabled={true}>
                <Spinner />
              </Button>
            ) : template ? (
              <Button size="small" onClick={handleSubmitDownloadTemplate}>
                Download Template
              </Button>
            ) : (
              <Button size="small" onClick={handleSubmitExportCanvas}>
                Export Canvas
              </Button>
            )}
          </div>
        </div>
      </Surface>
      <Surface backgroundColor="PrimeWhite" borderColor="GrayWarm5">
        <div className={styles.devtoolsInner}>
          <Text type="Body3">Set ZSL Creator Base URL</Text>
          <TextInput
            value={zslCreatorBaseUrl}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setZslCreatorBaseUrl(e.target.value)
            }
          />
        </div>
      </Surface>
    </div>
  );
};

export default DevTools;
